import { useEffect } from 'react'

/**
 * Render-time safety net for stored full-page overlay content.
 *
 * The clipping containment boundary on the wrapper is the load-bearing control: it confines
 * every descendant — trusted or injected, inline- or class-positioned — to the post box, so
 * injected `absolute`/`sticky` overlays cannot escape it. This observer is a source-agnostic
 * fallback for the one primitive the boundary can miss on browsers without paint-containment
 * support: `position: fixed`. Because `fixed` is never used by any trusted rendered chrome
 * (verified: image/code/embed/poll/etc. use only `absolute`/`sticky`), it can be neutralized
 * unconditionally — with no content-derived, attacker-spoofable exclusion list at all.
 */

// Dedup state lives in-memory, never in the DOM: the scanned content is attacker-controlled,
// so a DOM flag could be spoofed to mark a node as already-handled and skip neutralization.
const neutralizedElements = new WeakSet<Element>()

// Warn at most once per session: the trigger is attacker-controlled content, so a per-element
// warning lets a post with thousands of fixed nodes flood the console.
let hasWarned = false

// Test-only: `hasWarned` is module-lifetime state, so specs that assert on the warning would
// otherwise pass/fail depending on which spec ran first. Reset it between tests.
export const resetOverlayEscapeWarningForTests = (): void => {
  hasWarned = false
}

const neutralize = (element: HTMLElement): void => {
  neutralizedElements.add(element)
  element.style.setProperty('position', 'static', 'important')
  element.style.setProperty('max-width', '100%', 'important')
  element.style.setProperty('max-height', '100%', 'important')
  if (!hasWarned) {
    hasWarned = true
    // eslint-disable-next-line no-console
    console.warn('[HtmlContent] neutralized content-injected fixed overlay')
  }
}

const scan = (container: HTMLElement): void => {
  const elements = container.querySelectorAll<HTMLElement>('*')
  elements.forEach(element => {
    if (neutralizedElements.has(element)) {
      return
    }
    if (window.getComputedStyle(element).position === 'fixed') {
      neutralize(element)
    }
  })
}

export const useOverlayEscapeObserver = (
  container: HTMLElement | null,
  enabled = true,
): void => {
  useEffect(() => {
    if (!enabled || !container || typeof window === 'undefined') {
      return undefined
    }

    let frame = 0
    const scheduleScan = (): void => {
      if (frame) {
        return
      }
      frame = window.requestAnimationFrame(() => {
        frame = 0
        scan(container)
      })
    }

    scheduleScan()

    const mutationObserver = new MutationObserver(scheduleScan)
    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    })

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame)
      }
      mutationObserver.disconnect()
    }
    // Keyed off the node itself, not a ref object: an instance that mounts with an empty `value`
    // and receives content later remounts the boundary, so `container` changes from null to the
    // node and the effect re-runs and attaches. A stable ref object never would.
  }, [container, enabled])
}
